if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.initPersonalisationFields();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.validatePersonalisation()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.removeBlankOptionalPersonalisationProperties(formData);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings?.error || 'There was an error adding this item. Please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      initPersonalisationFields() {
        this.personalisationInputs = Array.from(this.form.querySelectorAll('[data-personalisation-input]'));
        this.personalisationRequiredFields = Array.from(this.form.querySelectorAll('[data-personalisation-required]'));
        this.personalisationConfirmations = Array.from(
          this.form.querySelectorAll('[data-personalisation-confirmation]')
        );
        this.personalisationFileStates = new WeakMap();

        const controls = Array.from(
          new Set([...this.personalisationInputs, ...this.personalisationRequiredFields])
        );

        controls.forEach((field) => {
          this.updatePersonalisationCounter(field);
          this.updatePersonalisationFileStatus(field, { initialize: true });
          const updateControl = () => {
            this.normalisePersonalisationField(field);
            this.syncPersonalisationControl(field);
            this.syncPersonalisationConfirmationState();
            this.clearPersonalisationError();
            this.updatePersonalisationCounter(field);
            this.updatePersonalisationFileStatus(field);
          };
          field.addEventListener('input', updateControl);
          field.addEventListener('change', updateControl);
        });

        this.form.addEventListener('reset', () => {
          requestAnimationFrame(() => {
            controls
              .filter((field) => field.type === 'file')
              .forEach((field) => this.updatePersonalisationFileStatus(field));
            this.syncPersonalisationConfirmationState();
          });
        });

        this.personalisationConfirmations.forEach((confirmation) => {
          confirmation.addEventListener('change', () => {
            this.syncPersonalisationControl(confirmation);
            this.syncPersonalisationConfirmationState();
            this.clearPersonalisationError();
          });
        });

        this.syncPersonalisationConfirmationState();
      }

      syncPersonalisationConfirmationState() {
        const spellingInputs = Array.from(
          this.form.querySelectorAll('[data-personalisation-spelling]')
        );
        const hasSpelling = spellingInputs.some((field) => this.hasPersonalisationValue(field));

        this.personalisationConfirmations.forEach((confirmation) => {
          const wrapper = confirmation.closest('[data-personalisation-confirmation-wrapper]');
          const active = confirmation.dataset.personalisationConfirmationMode === 'photo'
            ? true
            : hasSpelling;
          confirmation.disabled = !active;
          confirmation.required = active;
          if (active) {
            confirmation.setAttribute('aria-required', 'true');
          } else {
            confirmation.checked = false;
            confirmation.removeAttribute('aria-required');
            this.clearPersonalisationControl(confirmation);
          }
          if (wrapper) wrapper.hidden = !active;
        });

        return this.personalisationConfirmations.some((confirmation) => !confirmation.disabled);
      }

      validatePersonalisation({ includeConfirmation = true } = {}) {
        const requiredFields = Array.from(this.form.querySelectorAll('[data-personalisation-required]'));
        const personalisationInputs = Array.from(this.form.querySelectorAll('[data-personalisation-input]'));
        const fieldsToValidate = Array.from(new Set([...requiredFields, ...personalisationInputs]));
        const confirmations = includeConfirmation ? Array.from(
          this.form.querySelectorAll('[data-personalisation-confirmation]')
        ).filter((confirmation) => !confirmation.disabled)
          : [];

        const invalidContractField = this.form.querySelector('[data-personalisation-contract-invalid]');
        if (invalidContractField) {
          this.showPersonalisationError(
            invalidContractField,
            this.getPersonalisationContractError(invalidContractField)
          );
          return false;
        }

        const duplicatePropertyField = this.getDuplicatePersonalisationPropertyField(personalisationInputs);
        if (duplicatePropertyField) {
          this.showPersonalisationError(
            duplicatePropertyField,
            this.getPersonalisationContractError(duplicatePropertyField)
          );
          return false;
        }

        if (!fieldsToValidate.length && !confirmations.length) return true;

        for (const field of fieldsToValidate) {
          if (!field.hasAttribute('data-personalisation-required') && !this.hasPersonalisationValue(field)) {
            this.clearPersonalisationControl(field);
            continue;
          }

          const message = this.getPersonalisationFieldError(field);
          if (message) {
            this.showPersonalisationError(field, message);
            return false;
          }
          this.clearPersonalisationControl(field);
        }

        if (!includeConfirmation) {
          const confirmationOwnsError = this.form.querySelector(
            '[data-personalisation-confirmation][aria-invalid="true"]'
          );
          if (!confirmationOwnsError) this.handleErrorMessage();
          return true;
        }

        for (const confirmation of confirmations) {
          if (!confirmation.checked) {
            const message =
              confirmation.dataset.errorMessage ||
              'Please confirm your personalisation details before adding this item to your basket.';
            this.showPersonalisationError(confirmation, message);
            return false;
          }
          this.clearPersonalisationControl(confirmation);
        }

        this.clearPersonalisationError(true);
        return true;
      }

      getPersonalisationFieldError(field) {
        if (!this.hasPersonalisationValue(field)) {
          return (
            field.dataset.errorMessage ||
            'Please add your personalisation details before adding this item to your basket.'
          );
        }

        if (field.validity && field.validity.patternMismatch) {
          return field.dataset.patternMessage || field.validationMessage;
        }

        if (
          field.dataset.personalisationType === 'number' &&
          field.value &&
          /\D/.test(field.value)
        ) {
          return field.dataset.patternMessage || field.validationMessage;
        }

        if (
          field.type === 'file' &&
          field.files?.[0] &&
          !this.fileMatchesAccept(field.files[0], field.getAttribute('accept'))
        ) {
          return field.dataset.fileTypeMessage || field.dataset.errorMessage;
        }

        const minLength = Number(field.dataset.personalisationMinlength || field.getAttribute('minlength')) || 0;
        if (minLength > 0 && field.value && field.value.trim().length < minLength) {
          return field.dataset.minlengthMessage || field.validationMessage || field.dataset.errorMessage;
        }

        return '';
      }

      getPersonalisationContractError(control) {
        return (
          control.closest('[data-personalisation-field]')?.dataset.personalisationContractError ||
          'This product’s personalisation options need attention. Please contact us before ordering.'
        );
      }

      getDuplicatePersonalisationPropertyField(fields = []) {
        const propertyNames = new Set();

        for (const field of fields) {
          if (!field.name || !field.name.startsWith('properties[')) continue;
          if (propertyNames.has(field.name)) return field;
          propertyNames.add(field.name);
        }

        return null;
      }

      fileMatchesAccept(file, accept) {
        if (!file || !accept) return true;

        const acceptedTypes = accept
          .split(',')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);
        if (!acceptedTypes.length) return true;

        const fileName = (file.name || '').toLowerCase();
        const mimeType = (file.type || '').toLowerCase();

        return acceptedTypes.some((acceptedType) => {
          if (acceptedType.startsWith('.')) return fileName.endsWith(acceptedType);
          if (acceptedType.endsWith('/*')) return mimeType.startsWith(acceptedType.slice(0, -1));
          return mimeType === acceptedType;
        });
      }

      removeBlankOptionalPersonalisationProperties(formData) {
        const optionalFields = Array.from(
          this.form.querySelectorAll('[data-personalisation-input]:not([data-personalisation-required])')
        );

        for (const field of optionalFields) {
          if (!field.name || this.hasPersonalisationValue(field)) continue;

          const sameNameFields = Array.from(this.form.elements).filter(
            (candidate) => candidate !== field && candidate.name === field.name
          );
          if (sameNameFields.some((candidate) => this.hasPersonalisationValue(candidate))) continue;

          formData.delete(field.name);
        }
      }

      normalisePersonalisationField(field) {
        if (!field || field.dataset.personalisationType !== 'number' || field.type === 'file') return;

        const cleanedValue = field.value.replace(/\D/g, '');
        if (field.value !== cleanedValue) field.value = cleanedValue;
      }

      hasPersonalisationValue(field) {
        if (!field) return true;
        if (field.type === 'file') return field.files && field.files.length > 0;
        if (field.type === 'checkbox' || field.type === 'radio') return field.checked;
        return Boolean(field.value && field.value.trim());
      }

      showPersonalisationError(control, message) {
        const disclosure = control.closest('details.hf-personalisation-field__optional');
        if (disclosure) disclosure.open = true;
        this.setPersonalisationControlError(control, message);
        control.focus({ preventScroll: true });
        const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        control.scrollIntoView({ behavior, block: 'center' });
      }

      setPersonalisationControlError(control, message) {
        if (this.personalisationErrorControl && this.personalisationErrorControl !== control) {
          this.clearPersonalisationControl(this.personalisationErrorControl);
        }

        const item = control.closest('[data-personalisation-item]');
        const wrapper = control.closest('[data-personalisation-field]');
        control.setCustomValidity(message);
        control.setAttribute('aria-invalid', 'true');
        if (item) item.classList.add('is-invalid');
        if (wrapper) wrapper.classList.add('is-invalid');
        this.personalisationErrorControl = control;
        this.handleErrorMessage(message);

        const errorMessage = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (errorMessage?.id && !errorMessage.hidden && this.errorMessage?.textContent) {
          control.setAttribute('aria-errormessage', errorMessage.id);
        } else {
          control.removeAttribute('aria-errormessage');
        }
      }

      clearPersonalisationError(force = false) {
        const personalisationInputs = Array.from(this.form.querySelectorAll('[data-personalisation-input]'));
        const confirmations = Array.from(
          this.form.querySelectorAll('[data-personalisation-confirmation]')
        ).filter((confirmation) => !confirmation.disabled);
        const fieldsValid = personalisationInputs.every((field) => {
          const shouldValidate = field.hasAttribute('data-personalisation-required') || this.hasPersonalisationValue(field);
          return !shouldValidate || !this.getPersonalisationFieldError(field);
        });
        const confirmationsValid = confirmations.every((confirmation) => confirmation.checked);

        if (!force && (!fieldsValid || !confirmationsValid)) return;

        [...personalisationInputs, ...confirmations].forEach((control) => this.clearPersonalisationControl(control));
        this.handleErrorMessage();
      }

      syncPersonalisationControl(control) {
        const shouldValidate = control.required || control.hasAttribute('data-personalisation-required') || this.hasPersonalisationValue(control);
        const message = shouldValidate ? this.getPersonalisationFieldError(control) : '';
        if (!message) {
          const ownsError = this.personalisationErrorControl === control;
          this.clearPersonalisationControl(control);
          if (ownsError) this.handleErrorMessage();
          return;
        }

        this.setPersonalisationControlError(control, message);
      }

      clearPersonalisationControl(control) {
        const item = control.closest('[data-personalisation-item]');
        const wrapper = control.closest('[data-personalisation-field]');
        control.setCustomValidity('');
        control.removeAttribute('aria-invalid');
        control.removeAttribute('aria-errormessage');
        if (item) item.classList.remove('is-invalid');
        if (wrapper && !wrapper.querySelector('[aria-invalid="true"]')) wrapper.classList.remove('is-invalid');
        if (this.personalisationErrorControl === control) this.personalisationErrorControl = null;
      }

      updatePersonalisationFileStatus(field, { initialize = false } = {}) {
        if (!field || field.type !== 'file') return false;

        const item = field.closest('[data-personalisation-item]') || field.closest('[data-personalisation-field]');
        const status = item?.querySelector('[data-personalisation-file-status]');
        if (!status) return false;

        const file = field.files?.[0] || null;
        const empty = status.dataset.emptyText || '';
        const prefix = status.dataset.selectedPrefix || '';
        const replace = status.dataset.replaceText || '';
        const text = file ? [prefix, file.name, replace].filter(Boolean).join(' ') : empty;
        const previous = this.personalisationFileStates.get(field);
        this.personalisationFileStates.set(field, { file, text });
        if (initialize || (previous?.file === file && previous?.text === text) || status.textContent === text) {
          return false;
        }
        status.textContent = text;
        return true;
      }

      updatePersonalisationCounter(field) {
        if (!field) return;

        const item = field.closest('[data-personalisation-item]');
        const fallbackWrapper = field.closest('[data-personalisation-field]');
        const counterScope = item || fallbackWrapper;
        const counter = counterScope ? counterScope.querySelector('[data-personalisation-counter]') : null;

        if (counter) {
          const max = Number(field.dataset.personalisationMaxlength || field.getAttribute('maxlength')) || 0;
          const current = field.value ? field.value.length : 0;
          counter.textContent = max ? `${current}/${max}` : current;
          counter.classList.toggle('is-near-limit', max > 0 && current >= max * 0.85);
        }

        if (field.hasAttribute('data-personalisation-preview-source') && fallbackWrapper) {
          const preview = fallbackWrapper.querySelector('[data-personalisation-preview]');
          if (preview) {
            const emptyText = fallbackWrapper.dataset.personalisationPreviewEmpty || '';
            preview.textContent = field.value.trim() || emptyText;
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        this.errorMessage.textContent = errorMessage || '';
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
